import { Inject, Injectable, InjectionToken } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { RoiStore, RoiChangeStore, RoiStatsStore } from '../../stores';
import { SyncRoiChangesUseCase } from '@analysis/use-cases';
import { FindingVersionRef } from '@analysis/domain';
import { catchError, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, from, Observable, of, timer } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { SnackbarService } from '@shared/ui';

export type SyncChangesPayload = {
  sampleId: string;
  sourceAnalystId: string;
  currentAnalystId: string;
};

export const SYNC_CHANGES_USE_CASE = new InjectionToken<SyncRoiChangesUseCase>('SYNC_CHANGES_USE_CASE');

const SNACKBAR_DEBOUNCE_MS = 300;

/**
 * The finding versions a save produced, as `findingId -> version`.
 *
 * Highest version per finding, because one save touches several ROIs of the same finding
 * and each returned row carries that finding's new version. These are post-save
 * versions: `updateRois` returns the rows mapped back from what Parse actually saved, so
 * no read is needed to learn them.
 *
 * Derived from the saved rows only, NOT from `deletedRois` - those are pre-save
 * snapshots, so their version is the one *before* the deletion and asserting it would be
 * a weaker claim than making none.
 *
 * One gap follows from that, and it is worth knowing rather than papering over: a save
 * whose last remaining ROI on a finding is deleted produces a new finding version that
 * maps to no rows at all, so it cannot appear here and the poll will not wait for it.
 * The projection still corrects the table; what is lost is only the waiting. Closing it
 * means having `SyncRoiChangesUseCase` return the saved finding versions directly
 * instead of leaving them to be inferred from rows.
 */
function toFindingVersions(
  rois: { findingId?: string; findingVersion?: number; findingVersionId?: string }[]
): Map<string, FindingVersionRef> {
  const versions = new Map<string, FindingVersionRef>();

  for (const roi of rois) {
    if (!roi.findingId || roi.findingVersion === undefined || !roi.findingVersionId) continue;

    const held = versions.get(roi.findingId);
    if (held && held.version >= roi.findingVersion) continue;

    versions.set(roi.findingId, { version: roi.findingVersion, versionId: roi.findingVersionId });
  }

  return versions;
}

@Injectable()
export class RoiSyncEffects {
  private snackbarRef: MatSnackBarRef<unknown> | undefined;
  private readonly snackbarTrigger$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly roiStatsStore: RoiStatsStore,
    @Inject(SYNC_CHANGES_USE_CASE)
    private readonly syncChangesUseCase: SyncRoiChangesUseCase,
    private readonly snackbarService: SnackbarService
  ) {
    this.wireSnackbar(this.snackbarTrigger$);
  }

  readonly syncChanges = this.roiStore.effect((payload$: Observable<SyncChangesPayload>) =>
    payload$.pipe(
      switchMap(({ sampleId, currentAnalystId }) => {
        this.roiChangeStore.setSyncing(true);
        this.snackbarTrigger$.next(true);
        return from(this.syncChangesUseCase.execute({ sampleId, currentAnalystId })).pipe(
          concatLatestFrom(() => this.roiStore.rois$),
          tap(([{ syncedRois, deletedRois, counterDeltas }, entities]) => {
            const roisToUpsert = syncedRois.filter((roi) => !!entities[roi.id]);
            this.roiStore.completeSyncWithSave({
              roisToUpsert,
              deletedRois: deletedRois.map((r) => r.id),
              allUpdatedRois: syncedRois,
            });
            this.roiStatsStore.applyCounterDeltas(counterDeltas);
            // What this client wrote, recorded here because this is the only place that
            // knows it: the rows come back from Parse carrying the versions the save
            // produced, so no read is needed to learn them. It excuses the projection from
            // holding those findings yet, and lets a page read that lags recognise its rows
            // as the older ones.
            //
            // MUST stay above `clearAllChanges()`, which is what flips `syncing` to
            // false and so is what makes the facade's `onSyncComplete$` fire. That edge
            // starts the projection poll, and the poll reads these off the store - set
            // them afterwards and the first tick compares against nothing.
            this.roiStatsStore.recordOwnSaveVersions(toFindingVersions(syncedRois));
            this.roiChangeStore.clearAllChanges();
          }),
          tap(() => this.snackbarService.show('analysis.saved')),
          map(() => undefined),
          catchError((error) => {
            this.roiChangeStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.snackbarTrigger$.next(false))
        );
      })
    )
  );

  private readonly wireSnackbar = this.roiStore.effect((flag$: Observable<boolean>) =>
    flag$.pipe(
      distinctUntilChanged(),
      switchMap((active) => (active ? timer(SNACKBAR_DEBOUNCE_MS).pipe(map(() => true)) : of(false))),
      tap((active: boolean) => {
        this.snackbarRef?.dismiss();
        this.snackbarRef = undefined;
        if (!active) return;
        this.snackbarRef = this.snackbarService.show('analysis.saving_changes', {
          duration: 0,
          dismissible: false,
        });
      })
    )
  );
}
